/**
 * CallTemplatesPanel
 *
 * Admin-only unified template manager: edits the 3 system templates
 * (Birthday / Absent / Announcement wrapper) AND lets admins create / edit /
 * delete any number of custom templates that show up as quick-picks in the
 * announcement modal.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  MessageSquareText, Save, RotateCcw, Loader2, Cake, AlertCircle, Megaphone,
  Sparkles, Plus, Trash2, FileText
} from "lucide-react";
import { toast } from "sonner";

const SYSTEM_ICONS = { birthday: Cake, absent: AlertCircle, announcement: Megaphone };
const SYSTEM_TONES = {
  birthday: "text-pink-500",
  absent: "text-amber-500",
  announcement: "text-violet-500",
};

export default function CallTemplatesPanel({ isOpen, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});    // id -> { label, template }
  const [busy, setBusy] = useState({});        // id -> bool
  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState({ label: "", template: "" });
  const [creating, setCreating] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // template row pending delete

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/call-templates");
      setRows(data || []);
      const d = {};
      (data || []).forEach((r) => { d[r.id] = { label: r.label, template: r.template }; });
      setDrafts(d);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  const saveRow = async (row) => {
    const d = drafts[row.id] || {};
    const label = (d.label || "").trim();
    const template = (d.template || "").trim();
    if (template.length < 10) { toast.error("Template must be at least 10 characters"); return; }
    if (!row.is_system && (label.length < 2 || label.length > 80)) {
      toast.error("Label must be 2–80 characters"); return;
    }
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      await api.put(`/admin/call-templates/${row.id}`, { label: label || row.label, template });
      toast.success(`${row.is_system ? "System" : "Custom"} template saved`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy((b) => ({ ...b, [row.id]: false }));
    }
  };

  const resetOrDeleteRow = async (row) => {
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      await api.delete(`/admin/call-templates/${row.id}`);
      toast.success(row.is_system ? "Reset to default" : "Template deleted");
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not remove");
    } finally {
      setBusy((b) => ({ ...b, [row.id]: false }));
    }
  };

  const createTemplate = async () => {
    const label = newDraft.label.trim();
    const template = newDraft.template.trim();
    if (label.length < 2) { toast.error("Label is required"); return; }
    if (template.length < 10) { toast.error("Template must be at least 10 characters"); return; }
    setCreating(true);
    try {
      await api.post("/admin/call-templates", { label, template });
      toast.success("Template created");
      setNewDraft({ label: "", template: "" });
      setShowNew(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not create template");
    } finally {
      setCreating(false);
    }
  };

  const systemRows = useMemo(() => rows.filter((r) => r.is_system), [rows]);
  const customRows = useMemo(() => rows.filter((r) => !r.is_system), [rows]);

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="call-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <MessageSquareText className="h-5 w-5 text-emerald-500" />
            Call message templates
          </DialogTitle>
          <DialogDescription>Create and manage reusable voice-call message templates for announcements.</DialogDescription>
        </DialogHeader>

        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-700/40 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
          The backend substitutes placeholders below (use either{" "}
          <code className="bg-slate-200 dark:bg-slate-900 px-1 rounded">{"{name}"}</code> or{" "}
          <code className="bg-slate-200 dark:bg-slate-900 px-1 rounded">{"{{name}}"}</code>) and sends the
          finished string to Retell as <code className="bg-slate-200 dark:bg-slate-900 px-1 rounded">final_message</code>.
          System templates power Birthday/Absent/Announcement calls. Custom templates appear as quick-picks in the announcement modal.
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {/* System templates */}
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">System (always present)</div>
              {systemRows.map((r) => <TemplateCard key={r.id} row={r} drafts={drafts} setDrafts={setDrafts} busy={busy} onSave={saveRow} onReset={() => resetOrDeleteRow(r)} />)}
            </div>

            {/* Custom templates */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Custom (announcement quick-picks)</div>
                <Button
                  size="sm"
                  onClick={() => setShowNew((v) => !v)}
                  className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600"
                  data-testid="tmpl-new-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> New template
                </Button>
              </div>
              {showNew && (
                <div className="border border-emerald-200 dark:border-emerald-900 rounded-xl p-4 bg-emerald-50/40 dark:bg-emerald-900/20" data-testid="tmpl-new-card">
                  <Input
                    value={newDraft.label}
                    onChange={(e) => setNewDraft((d) => ({ ...d, label: e.target.value }))}
                    placeholder="Label (e.g. PTM Reminder)"
                    className="h-9 rounded-lg mb-2 text-sm"
                    maxLength={80}
                    data-testid="tmpl-new-label"
                  />
                  <Textarea
                    value={newDraft.template}
                    onChange={(e) => setNewDraft((d) => ({ ...d, template: e.target.value }))}
                    placeholder="ನಮಸ್ಕಾರ {{parent_name}} ಅವರೇ. …"
                    rows={5}
                    className="rounded-lg text-sm font-mono"
                    data-testid="tmpl-new-input"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setShowNew(false); setNewDraft({ label: "", template: "" }); }} className="h-8 text-xs">Cancel</Button>
                    <Button size="sm" onClick={createTemplate} disabled={creating} className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600" data-testid="tmpl-new-save">
                      {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                      Create
                    </Button>
                  </div>
                </div>
              )}
              {customRows.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                  No custom templates yet. Click &quot;New template&quot; to add quick-picks for announcements.
                </div>
              ) : customRows.map((r) => (
                <TemplateCard
                  key={r.id}
                  row={r}
                  drafts={drafts}
                  setDrafts={setDrafts}
                  busy={busy}
                  onSave={saveRow}
                  onDelete={() => setConfirmDel(r)}
                />
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete confirmation for custom templates */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="tmpl-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>This permanently removes the template. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            &quot;{confirmDel?.label}&quot; will be removed permanently. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button
              onClick={() => resetOrDeleteRow(confirmDel)}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="tmpl-delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


function TemplateCard({ row, drafts, setDrafts, busy, onSave, onReset, onDelete }) {
  const d = drafts[row.id] || { label: row.label, template: row.template };
  const Icon = row.is_system ? (SYSTEM_ICONS[row.system_kind] || Sparkles) : FileText;
  const tone = row.is_system ? (SYSTEM_TONES[row.system_kind] || "text-slate-500") : "text-emerald-500";
  const isBusy = !!busy[row.id];
  const dirty = (d.label || "") !== (row.label || "") || (d.template || "") !== (row.template || "");

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800" data-testid={`template-card-${row.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center ${tone}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            {row.is_system ? (
              <div className="font-semibold text-slate-900 dark:text-slate-100">{row.label}</div>
            ) : (
              <Input
                value={d.label || ""}
                onChange={(e) => setDrafts((dd) => ({ ...dd, [row.id]: { ...dd[row.id], label: e.target.value } }))}
                className="h-8 text-sm font-semibold rounded-lg max-w-xs"
                maxLength={80}
                data-testid={`tmpl-label-${row.id}`}
              />
            )}
            <div className="text-[11px] text-slate-500">
              {row.is_system
                ? <>System template · last edit by {row.updated_by || "—"}{row.updated_at ? ` · ${new Date(row.updated_at).toLocaleString()}` : ""}</>
                : <>Custom · {row.updated_at ? new Date(row.updated_at).toLocaleString() : ""}</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {row.is_system ? (
            <Button size="sm" variant="ghost" onClick={onReset} disabled={isBusy} className="h-8 text-xs text-slate-600" data-testid={`tmpl-reset-${row.id}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to default
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={isBusy} className="h-8 text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" data-testid={`tmpl-delete-${row.id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onSave(row)}
            disabled={isBusy || !dirty}
            className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
            data-testid={`tmpl-save-${row.id}`}
          >
            {isBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <Textarea
        value={d.template ?? ""}
        onChange={(e) => setDrafts((dd) => ({ ...dd, [row.id]: { ...dd[row.id], template: e.target.value } }))}
        rows={7}
        className="rounded-lg font-mono text-sm"
        placeholder="Template body…"
        data-testid={`tmpl-input-${row.id}`}
      />

      {!!(row.placeholders || []).length && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Placeholders:</span>
          {(row.placeholders || []).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setDrafts((dd) => ({ ...dd, [row.id]: { ...dd[row.id], template: (dd[row.id]?.template || "") + `{{${p}}}` } }))}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
              data-testid={`tmpl-ph-${row.id}-${p}`}
            >
              {`{{${p}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
